import { getWriteSignedUrl } from "frontend/api";
import { SyncService } from "frontend/services/syncService";
import tracking from "frontend/tracking";
import { useAtomValue, useSetAtom } from "jotai";
import { nanoid } from "nanoid";
import { SetStateAction, useCallback } from "react";
import consts from "shared/consts";
import { NewElementData } from "shared/datamodel/canvas-element";

import { placeFile } from "shared/datamodel/file";
import { placePdf } from "shared/datamodel/pdf";
import { extractPdfDimensions, calculatePdfDisplayDimensions } from "frontend/utils/pdf-utils";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import { FileMetadataType, FileUploadState, Point } from "shared/datamodel/schemas";
import { dbKey } from "shared/util/utils";
import { boardAtom, imageCacheAtom, stageRefAtom, syncServiceAtom, userAtom } from "state-atoms";
import { buildMetadataEntry, buildUploadUrl } from "utils/file-utils";
import { viewportToStage } from "utils/math-utils";
import type { UppyFile } from "@uppy/core";

interface ImageWithThumbnail {
  image: HTMLImageElement;
  thumbnail?: string;
}

const THUMBNAIL_MAX_SIZE = 100;

function createImageThumbnail(img: HTMLImageElement, fileType: string) {
  const canvas = document.createElement("canvas");
  const canvasContext = canvas.getContext("2d");
  if (!canvasContext) {
    return null;
  }

  // Set thumbnail dimensions (max 200px on the longest side)
  let width = img.width;
  let height = img.height;

  if (width > height) {
    if (width > THUMBNAIL_MAX_SIZE) {
      height = (height * THUMBNAIL_MAX_SIZE) / width;
      width = THUMBNAIL_MAX_SIZE;
    }
  } else {
    if (height > THUMBNAIL_MAX_SIZE) {
      width = (width * THUMBNAIL_MAX_SIZE) / height;
      height = THUMBNAIL_MAX_SIZE;
    }
  }

  canvas.width = width;
  canvas.height = height;
  canvasContext.drawImage(img, 0, 0, width, height);

  // Determine format based on file type
  const isTransparentFormat = /^(image\/(png|gif|webp))$/.test(fileType.toLowerCase());

  // Use PNG for transparent formats, JPEG for others
  return canvas.toDataURL(isTransparentFormat ? "image/png" : "image/jpeg", 0.6);
}

function getImagesWithThumbnails(
  files: File[],
  setImageCache: (update: SetStateAction<Record<string, HTMLImageElement>>) => void
) {
  return files.map(
    (file) =>
      new Promise<ImageWithThumbnail>((resolve, reject) => {
        const img = new Image();
        img.hidden = true;
        img.src = (window.URL || window.webkitURL).createObjectURL(file);

        const processImage = () => {
          const key = nanoid();
          img.id = key;

          setImageCache((current) => ({ ...current, [key]: img }));

          const thumbnail = createImageThumbnail(img, file.type);

          if (thumbnail) {
            resolve({ image: img, thumbnail });
          } else {
            resolve({ image: img });
          }
        };

        // Try decode() first (modern browsers)
        img
          .decode()
          .then(processImage)
          .catch((decodeError) => {
            console.error("Image decode failed:", decodeError);
            tracking.trackAnalyticsEvent("image-decode-failed");

            img.onload = () => {
              processImage();
            };

            img.onerror = () => {
              tracking.trackAnalyticsEvent("image-load-failed");
              reject();
            };

            // If image is already loaded (cached), call onload immediately
            if (img.complete) {
              img.onload = null;
              img.onerror = null;
              processImage();
            }
          });
      })
  );
}

export function usePerformUpload(
  onAddElements: (
    elements: NewElementData[],
    addUndo?: boolean,
    source?: string,
    sequenceId?: string
  ) => Promise<string[]>,
  onDeleteElements: (ids: string[], addUndo?: boolean) => void,
  onError: (errorMessage: string) => void
) {
  const setImageCache = useSetAtom(imageCacheAtom);
  const stageRef = useAtomValue(stageRefAtom);
  const board = useAtomValue(boardAtom);
  const user = useAtomValue(userAtom);
  const syncService = useAtomValue(syncServiceAtom) as SyncService<RW> | null;

  return useCallback(
    async (files: File[], clickPosition?: Point) => {
      const posScale = stageRef?.current ? { ...stageRef.current.position(), scale: stageRef.current.scaleX() } : null;
      const posFromStage = posScale
        ? viewportToStage(posScale, { x: window.innerWidth / 2, y: window.innerHeight / 2 })
        : null;

      const pos = clickPosition ?? posFromStage;
      const waitingForUploadFiles = new Set<string>();
      let isUploading = true;

      const handleBeforeUnload = () => {
        if (isUploading && waitingForUploadFiles.size > 0) {
          onDeleteElements([...waitingForUploadFiles], false);

          for (const id of waitingForUploadFiles) {
            syncService?.mutate.deleteMetadata({
              id,
            });
          }
        }
      };

      window.addEventListener("beforeunload", handleBeforeUnload);

      try {
        const metadata: { url: string; limitedSlot?: number }[] = files.map((f) => ({
          url: buildUploadUrl(board!.accountId, board!.documentId, f.name),
        }));

        // Separate PDFs from images
        const imageFiles: File[] = [];
        const imageIndices: number[] = [];
        const pdfFiles: { file: File; index: number }[] = [];

        files.forEach((file, index) => {
          if (file.type === "application/pdf") {
            pdfFiles.push({ file, index });
          } else {
            imageFiles.push(file);
            imageIndices.push(index);
          }
        });

        // Process only image files for thumbnails
        const imagesResults = await Promise.allSettled(getImagesWithThumbnails(imageFiles, setImageCache));

        // Create a map to get image results by original file index
        const imageResultsMap = new Map<number, PromiseSettledResult<ImageWithThumbnail>>();
        imageIndices.forEach((originalIndex, resultIndex) => {
          imageResultsMap.set(originalIndex, imagesResults[resultIndex]);
        });

        let failed = 0;
        const uploadPromises = [];
        for (const [i, file] of files.entries()) {
          const url = metadata[i].url;
          const isPdf = file.type === "application/pdf";
          const imgR = imageResultsMap.get(i);

          if (isPdf) {
            // Handle PDF upload
            const metadataEntry = buildMetadataEntry(
              file.name,
              file.type,
              null,
              user?.id,
              metadata[i].limitedSlot,
              nanoid(),
              FileUploadState.InProgress
            );
            const metadataId = dbKey(metadataEntry.id, FileMetadataType);

            // Store position for this file
            const pdfPosition = { x: pos.x, y: pos.y };
            pos.x += 50;
            pos.y += 50;

            uploadPromises.push(
              (async () => {
                let id = "";
                try {
                  // Extract PDF dimensions before creating the element
                  const pdfInfo = await extractPdfDimensions(file);
                  const displayDimensions = calculatePdfDisplayDimensions(pdfInfo.width, pdfInfo.height);

                  const pdfElement = placePdf(
                    pdfPosition,
                    metadataEntry.id,
                    "in-progress",
                    displayDimensions.width,
                    displayDimensions.height,
                    pdfInfo.totalPages
                  );

                  await syncService?.mutate.putMetadata({
                    id: metadataId,
                    value: {
                      ...metadataEntry,
                    },
                  });

                  const ids = await onAddElements(
                    [
                      {
                        id: pdfElement.id,
                        type: consts.CANVAS_ELEMENTS.PDF,
                        element: pdfElement.pdf,
                      },
                    ],
                    true,
                    "upload-pdf"
                  );

                  if (ids.length > 0) {
                    id = ids[0];
                    waitingForUploadFiles.add(id);
                  }

                  const data = await getWriteSignedUrl(url, file.type);

                  const response = await fetch(data.url, {
                    method: "PUT",
                    body: file,
                    headers: {
                      "Content-Type": file.type,
                    },
                  });

                  if (!response.ok) {
                    throw new Error("Upload failed");
                  }

                  syncService?.mutate.changeMetadata({
                    id: metadataId,
                    value: {
                      ...metadataEntry,
                      uploadState: FileUploadState.Completed,
                      url,
                    },
                  });

                  waitingForUploadFiles.delete(id);

                  tracking.trackAnalyticsEvent("uploaded-pdf", {
                    category: consts.TRACKING_CATEGORY.CANVAS_ACTION,
                    stringValue: file.type,
                    property: board?.documentId,
                  });
                } catch (error) {
                  console.error("Upload failed:", error);
                  waitingForUploadFiles.delete(id);

                  tracking.trackAnalyticsEvent("uploaded-pdf-failed", {
                    category: consts.TRACKING_CATEGORY.FILES,
                    stringValue: file.type,
                    property: board?.documentId,
                  });

                  if (id) {
                    onDeleteElements([id], false);
                    syncService?.mutate.deleteMetadata({ id: metadataId });
                  }
                  failed++;
                }
              })()
            );
          } else if (imgR && imgR.status === "fulfilled") {
            const metadataEntry = buildMetadataEntry(
              file.name,
              file.type,
              null,
              user?.id,
              metadata[i].limitedSlot,
              imgR.value.image.id,
              FileUploadState.InProgress
            );
            const metadataId = dbKey(metadataEntry.id, FileMetadataType);

            const fileElement = placeFile(
              { x: pos.x - imgR.value.image.width / 2, y: pos.y - imgR.value.image.height / 2 },
              "image",
              metadataEntry.id,
              "in-progress",
              imgR.value.image.width,
              imgR.value.image.height,
              imgR.value.thumbnail
            );

            pos.x += imgR.value.image.width / 10;
            pos.y += imgR.value.image.height / 10;

            uploadPromises.push(
              (async () => {
                let id = "";
                try {
                  await syncService?.mutate.putMetadata({
                    id: metadataId,
                    value: {
                      ...metadataEntry,
                    },
                  });

                  const ids = await onAddElements(
                    [
                      {
                        id: fileElement.id,
                        type: consts.CANVAS_ELEMENTS.FILE,
                        element: fileElement.file,
                      },
                    ],
                    true,
                    "upload-file"
                  );

                  if (ids.length > 0) {
                    id = ids[0];
                    waitingForUploadFiles.add(id);
                  }

                  const data = await getWriteSignedUrl(url, file.type);

                  const response = await fetch(data.url, {
                    method: "PUT",
                    body: file,
                    headers: {
                      "Content-Type": file.type,
                    },
                  });

                  if (!response.ok) {
                    throw new Error("Upload failed");
                  }

                  syncService?.mutate.changeMetadata({
                    id: metadataId,
                    value: {
                      ...metadataEntry,
                      uploadState: FileUploadState.Completed,
                      url,
                    },
                  });

                  waitingForUploadFiles.delete(id);

                  tracking.trackAnalyticsEvent("uploaded-single-image", {
                    category: consts.TRACKING_CATEGORY.CANVAS_ACTION,
                    stringValue: file.type,
                    property: board?.documentId,
                  });
                } catch (error) {
                  console.error("Upload failed:", error);
                  waitingForUploadFiles.delete(id);

                  tracking.trackAnalyticsEvent("uploaded-single-image-failed", {
                    category: consts.TRACKING_CATEGORY.FILES,
                    stringValue: file.type,
                    property: board?.documentId,
                  });

                  setImageCache((current) => {
                    const newCache = { ...current };
                    delete newCache[imgR.value.image.id];
                    return newCache;
                  });

                  if (id) {
                    onDeleteElements([id], false);
                  }

                  syncService?.mutate.deleteMetadata({
                    id: metadataId,
                  });

                  throw error;
                }
              })()
            );
          } else {
            failed++;
          }
        }
        const uploadResults = await Promise.allSettled(uploadPromises);

        for (const result of uploadResults) {
          if (result.status === "rejected") {
            failed++;
          }
        }

        if (failed > 0) {
          onError(`Failed to upload ${failed} files`);

          tracking.trackAnalyticsEvent("upload-images-failed", {
            failedCount: failed,
          });
        }
      } catch (error) {
        handleBeforeUnload();
        console.error("Upload process failed:", error);
        onError("Failed to upload files");
      } finally {
        isUploading = false;
        window.removeEventListener("beforeunload", handleBeforeUnload);
      }
    },
    [stageRef, onDeleteElements, syncService?.mutate, setImageCache, board, user?.id, onAddElements, onError]
  );
}

export interface TrackUploadFilesParams {
  files: UppyFile[];
  boardDocumentId?: string;
}

/**
 * Tracks file upload events for analytics.
 * Separates files by type and tracks both batch and individual file events.
 *
 * @param files - Array of Uppy files to track
 * @param boardDocumentId - Optional board document ID for tracking context
 */
export function trackUploadFiles({ files, boardDocumentId }: TrackUploadFilesParams): void {
  // Separate files by type for proper tracking
  const imageFiles = files.filter((file) => file.type?.startsWith("image/"));
  const pdfFiles = files.filter((file) => file.type === "application/pdf");
  const otherFiles = files.filter((file) => !file.type?.startsWith("image/") && file.type !== "application/pdf");

  // Track batch events based on file types
  if (imageFiles.length > 0) {
    tracking.trackEvent(consts.TRACKING_CATEGORY.CANVAS_ACTION, "uploaded-images", imageFiles.length, boardDocumentId);
  }
  if (pdfFiles.length > 0) {
    tracking.trackEvent(consts.TRACKING_CATEGORY.CANVAS_ACTION, "uploaded-pdfs", pdfFiles.length, boardDocumentId);
  }
  if (otherFiles.length > 0) {
    tracking.trackEvent(consts.TRACKING_CATEGORY.CANVAS_ACTION, "uploaded-files", otherFiles.length, boardDocumentId);
  }

  // Track individual file events based on type
  for (const file of files) {
    if (file.type?.startsWith("image/")) {
      tracking.trackEvent(
        consts.TRACKING_CATEGORY.CANVAS_ACTION,
        "uploaded-single-image",
        file.extension,
        boardDocumentId
      );
    } else if (file.type === "application/pdf") {
      tracking.trackEvent(
        consts.TRACKING_CATEGORY.CANVAS_ACTION,
        "uploaded-single-pdf",
        file.extension,
        boardDocumentId
      );
    } else {
      tracking.trackEvent(
        consts.TRACKING_CATEGORY.CANVAS_ACTION,
        "uploaded-single-file",
        file.extension,
        boardDocumentId
      );
    }
  }
}
